import React from 'react'
import { Message } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'

import Header from './Header'
import Body from './Body'
import {
  IBodyCell,
  IBodyRow,
  IColumn,
  PinnedStatus,
  onColumnMove,
  onColumnResize,
  onColumnResizeCommit,
  onDoubleClick,
  onFilterChangeWrapped,
  onHoverPopup,
  onRowClassName,
  onRowHover
} from './Types'
import { getScrollbarSize } from './ScrollUtil'
import BodySizes from './BodySizes'

export interface GridPanelProps {
  noDataPlaceholder?: React.ReactNode
  content: IBodyRow[]
  filter: PinnedStatus
  onHover: onRowHover
  onHoverPopup?: onHoverPopup
  onDoubleClick: onDoubleClick
  onRowClassName?: onRowClassName

  headerContent: IColumn[]
  commitHeaderResize: onColumnResizeCommit
  resizeHeader: onColumnResize
  onHeaderMove: onColumnMove
  onFilterChange: onFilterChangeWrapped

  headerRef?: React.RefObject<HTMLDivElement>
  bodyRef?: React.RefObject<HTMLDivElement>
  errorMessage?: JSX.Element

  hovered: number | null

  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void
  rowHeight: number
  /**
   * `fillerWidth` is responsible for calculating the correct
   * padding when there is or isn't a horizontal scroll bar.
   * */
  fillerWidth: number
  heightOffset: number
}

function GridPanel(props: GridPanelProps, ref: React.ForwardedRef<HTMLTableElement>) {
  const {
    noDataPlaceholder,
    headerContent,
    commitHeaderResize,
    resizeHeader,
    onHeaderMove,
    onFilterChange,
    onScroll,
    headerRef,
    bodyRef,
    content,
    heightOffset,
    errorMessage,
    fillerWidth,
    ...rest
  } = props

  const { t } = useTranslation()

  const filteredContent = React.useMemo(() => {
    return content.map(row => ({
      ...row,
      cols: row.cols.reduce<IBodyCell<any>[]>((columns, column) => {
        const header = headerContent.find(header => header.key === column.key)

        if (header) {
          const headerIsEqualToFilter = props.filter === header.pinned

          columns.push({
            ...column,
            visible: headerIsEqualToFilter
          })
        }

        return columns
      }, [])
    }))
  }, [content, headerContent, props.filter])

  const scrollbarSize = getScrollbarSize()
  const showScrollbarOffset = props.filter !== PinnedStatus.Center && fillerWidth === 0

  const bodyStyle = {
    overflow: 'scroll',
    height: `calc(100vh - ${240 + heightOffset}px)`,
    maxHeight: `calc(100vh - ${240 + heightOffset}px)`,
    marginBottom: `-${showScrollbarOffset ? scrollbarSize : '0'}px`,
    paddingBottom: '0px',
    backgroundColor: 'white',
    overflowX: 'auto'
  } as const

  const totalColumnsWidth = headerContent.reduce((total, header) => total + header.width, 0)

  const leftPinnedColumnsWidth = headerContent.reduce((total, header) => {
    if (header.pinned === PinnedStatus.Left) return total + header.width

    return total
  }, 0)

  const rightPinnedColumnsWidth = headerContent.reduce((total, header) => {
    if (header.pinned === PinnedStatus.Right) return total + header.width

    return total
  }, 0)

  // The filler column is only important for the center table so we set it to 0 for others.
  const fillerColumnWidth = props.filter === PinnedStatus.Center ? fillerWidth : 0

  return (
    <React.Fragment>
      <Header
        headers={headerContent}
        onHeaderResizeCommit={commitHeaderResize}
        onHeaderResize={resizeHeader}
        onHeaderMove={onHeaderMove}
        onFilterChange={onFilterChange}
        headerRef={headerRef}
        filter={props.filter}
        fillerWidth={fillerColumnWidth}
        onScroll={onScroll}
      />
      <div
        className={`cbgms-grid-body${props.filter !== PinnedStatus.Center ? '-inner' : ''}`}
        style={bodyStyle}
        ref={bodyRef}
        onScroll={onScroll}
      >
        {props.content.length === 0 && props.filter === PinnedStatus.Center && (
          <div style={{ width: totalColumnsWidth, paddingLeft: leftPinnedColumnsWidth, paddingRight: rightPinnedColumnsWidth }}>
            {errorMessage || <Message style={{ margin: 10 }}>{noDataPlaceholder || t('grid.nodata')}</Message>}
          </div>
        )}
        <table className='cbgms-grid-fixed' style={{ width: totalColumnsWidth }} ref={ref}>
          <BodySizes filter={props.filter} headers={headerContent} fillerWidth={fillerColumnWidth} />
          <Body content={filteredContent} fillerHeight={showScrollbarOffset ? scrollbarSize : undefined} {...rest} />
        </table>
      </div>
    </React.Fragment>
  )
}

export default React.memo(React.forwardRef(GridPanel))
